// `@woo/api/extension/candidate/extension-alert-candidate.api.ng1.ts` — the candidates behind
// an alert (plan §7 phase 3, slice 3).
import { request } from './request.ts'
import { fetchFile, type DownloadedFile } from './download.ts'

/** `AlertCandidateSortBy`. */
export const AlertCandidateSortBy = {
  Relevancy: 'RELEVANCY',
  CvDate: 'CV_DATE',
} as const
export type AlertCandidateSortBy = (typeof AlertCandidateSortBy)[keyof typeof AlertCandidateSortBy]

/**
 * The candidate fields the slim row reads, taken from a live response rather than from the
 * DTO's declaration — the DTO carries thirty-odd fields and the row uses eleven.
 *
 * `cvDate` is epoch MILLIS, not an ISO string.
 */
export interface AlertCandidate {
  id: number
  /**
   * ⚠️ OPTIONAL, AND ABSENT ON MOST ROWS. A candidate the tenant has not been shown the name
   * of arrives without one; the UI substitutes `Candidate <externalId>` (`candidateName`).
   */
  name?: string | null
  externalId?: string
  /** The id the ATS opens a candidate by, where it differs from `externalId` (assign-to-job). */
  atsIdForOpeningCandidate?: string
  algorithm?: number
  lastPosition?: string | null
  lastCompany?: string | null
  location?: string | null
  cvDate?: number | null
  addedToJob?: boolean
  exported?: boolean
  hidden?: boolean
  /**
   * Epoch millis, set by the SERVER when the candidate is archived — `hideCandidates` returns
   * it so the row can show "Archived <date>" without a refetch.
   */
  hiddenDate?: number | undefined
  viewed?: boolean
  favorite?: boolean
  messaged?: boolean
  email?: string | null
  /** Whether the address is on the tenant's do-not-mail list; messaging skips it. */
  emailBlacklisted?: boolean | null
  /** Epoch millis. The slim row shows "Never" when it is absent. */
  lastReceivedMessage?: number | null
}

export interface AlertCandidateList {
  candidatePage: AlertCandidate[]
  maxResults?: number
}

/**
 * `IAlertCandidateFilter` — the six show-flags plus the CV-date window.
 *
 * ⚠️ EVERY FLAG IS A SEPARATE BOOLEAN, NOT AN ENUM, and the shown-filter options each set a
 * different combination of them (`shownCandidatesOptions`, ported in
 * `packages/ui/src/shortlist/candidateFilters.ts`). Sending the wrong one produces a list
 * that is plausibly shorter or longer and never errors — which is why the options are
 * transcribed as data and the mapping is one function with tests.
 *
 * `atsFilters` is upstream's seventh field. Omitted until the ATS filter editor lands; it is
 * a nested object the server treats as absent when it is not sent.
 */
export interface AlertCandidateFilter {
  lastRelevancyXDays?: number
  showFavoriteCandidatesOnly?: boolean
  showHiddenCandidates?: boolean
  hideViewedCandidates?: boolean
  showExportedCandidatesOnly?: boolean
  showMessagedCandidatesOnly?: boolean
  showActionedAndUnactionedCandidates?: boolean
}

export interface GetAlertCandidatesParams {
  alertId: number
  filter?: AlertCandidateFilter
  sortBy?: AlertCandidateSortBy
  /**
   * ⚠️ UPSTREAM SENDS THIS ON THE NORMAL LIST FETCH. `persistFilterAndSort` tells the server to
   * REMEMBER the filter and sort it was given, so the next session opens the alert the same
   * way. It is a write dressed as a read, which is why it is an explicit parameter here rather
   * than a default: a caller that sets it while probing would quietly change what the user
   * sees next time.
   */
  persistFilterAndSort?: boolean
  limitCandidateIds?: number[]
}

/**
 * ⚠️ A POST, NOT A GET, despite reading. The parameters go in a JSON body
 * (extension-alert-candidate.api.ng1.ts:396) — the filter alone is a dozen booleans and a
 * candidate-id list, which is what a query string cannot carry. Writing it as a GET would 405,
 * the same way `isSsoRequired` did when this client guessed the other way.
 */
export async function getCandidatesByAlertId(params: GetAlertCandidatesParams): Promise<AlertCandidateList> {
  return request<AlertCandidateList>('POST', '/chrome-extension-api/alert-candidate/getCandidatesByAlertId', params)
}

/**
 * `IAlertCandidateDetailsDto` — id, name and blacklist reason, for every candidate the filter
 * matches rather than for the page.
 *
 * ⚠️ IT IS A SECOND, LIGHTER CALL AND THAT IS WHY IT EXISTS. "Select all in results" checks
 * `data.allRelevantDetails` (`extension-alert-candidates.component.ng1.ts:2131`) — every
 * candidate behind the alert, not the twenty on screen — and the full rows would be a payload
 * of the whole result set to populate a set of ids. The `name` comes with it because the bulk
 * dialogs list who is about to be messaged.
 */
export interface AlertCandidateDetails {
  id: number
  name?: string
  blacklistReason?: string
}

export interface AlertCandidateDetailsList {
  details: AlertCandidateDetails[]
  /** How many of the matches were enriched — only this call answers it; `HunterResultsLoaded` reports it. */
  totalEnriched?: number
  maxResults?: number
}

/** Same params as the list — the details must be the ones the current filter matches. */
export async function getCandidateDetailsByAlertId(
  params: GetAlertCandidatesParams,
): Promise<AlertCandidateDetailsList> {
  return request<AlertCandidateDetailsList>(
    'POST',
    '/chrome-extension-api/alert-candidate/getCandidateDetailsByAlertId',
    params,
  )
}

/**
 * The three candidate-flag writes — `favorCandidates`, `hideCandidates`, `unhideCandidate`.
 *
 * ⚠️ THEY ARE WOO-SIDE AND NOTHING ELSE. These set a flag on the alert-candidate row; they do
 * not touch the customer's ATS and send no mail. That is what separates them from Export,
 * Add-to-job and Message on the same selection bar, which do reach outside.
 *
 * ⚠️ AND THEY ARE NOT SYMMETRIC, THOUGH NOT IN THE WAY THIS NOTE USED TO CLAIM. It said
 * "favouring is one-way"; it is one-way IN BULK. There is no `unfavorCandidates`, but there
 * is a singular `unfavorCandidate` — exactly as archiving has a singular `unhideCandidate`.
 * The profile's action strip toggles both, because it acts on one candidate; the selection
 * bar only ever adds, because it acts on a list.
 */
export async function favorCandidates(alertId: number, candidateIds: number[]): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-candidate/favorCandidates', { alertId, candidateIds })
}

/**
 * ⚠️ IT RETURNS THE `hiddenDate`, AND THE CALLER NEEDS IT. The row renders "Archived <date>"
 * off the value the SERVER chose, so discarding the response and setting a local `Date.now()`
 * puts a date on screen that the next fetch disagrees with.
 */
export async function hideCandidates(alertId: number, candidateIds: number[]): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-candidate/hideCandidates', { alertId, candidateIds })
}

/** ⚠️ SINGULAR — `candidateId`, not `candidateIds`. There is no bulk unarchive. */
export async function unhideCandidate(alertId: number, candidateId: number): Promise<void> {
  await request('POST', '/chrome-extension-api/alert-candidate/unhideCandidate', { alertId, candidateId })
}

/**
 * ⚠️ SINGULAR TOO, and its absence is why the profile's star looked broken. The snippet calls
 * `favorAlertEntity` and `unfavorAlertEntity` as a pair
 * (`extension-alert-candidate-snippet.component.ng1.ts:360`); with only the bulk `favorCandidates`
 * ported, a favourited candidate had no way back.
 */
export async function unfavorCandidate(alertId: number, candidateId: number): Promise<void> {
  await request('POST', '/chrome-extension-api/alert-candidate/unfavorCandidate', { alertId, candidateId })
}

/**
 * `viewCandidate` / `viewCandidates` — marking a candidate as seen.
 *
 * ⚠️ THESE REACH OUTSIDE WOO, WHICH NOTHING ABOUT THEIR NAMES SUGGESTS. Upstream's
 * `shortlist.candidate-actions.service.ng1.ts:279` calls `viewCandidate` and then emits
 * `OnCandidateViewed`, and careero's `AtsNoteOnCandidateViewService` turns that event into an
 * automated note inside the CUSTOMER'S ATS. So this belongs beside `addToAtsJobAsync` and
 * `exportCandidates` rather than beside `favorCandidates`, even though it reads like a
 * read-tracking ping. The two dormant `…/review/snippet` prefixes in
 * `packages/protocol/url-contracts.json` are the other end of the same integration.
 *
 * ⚠️ AND A FAILURE IS SWALLOWED, DELIBERATELY. Upstream wraps both in try/catch with a
 * `console.log` and no user-visible effect ("exception thrown when viewing candidate,
 * suppressing"): the recruiter asked to see a profile, not to record that they saw it, so a
 * failed note must not interrupt them. Callers keep that rule.
 */
export async function viewCandidate(alertId: number, candidateId: number): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-candidate/viewCandidate', { alertId, candidateId })
}

/** The bulk form — `viewCandidates`, for a selection that is actioned rather than opened. */
export async function viewCandidates(alertId: number, candidateIds: number[]): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-candidate/viewCandidates', { alertId, candidateIds })
}

/**
 * `exportCandidates` — the SECOND write of an export, and not the one that reaches the ATS.
 *
 * ⚠️ IT DOES NOT PERFORM THE EXPORT. `exportToAtsList` (`export.ts`) pushes the profiles; this
 * records inside Woo that they went, which is what draws the row's "Exported" state and feeds
 * the usage report. Upstream fires it only AFTER the status poll reports success
 * (`extension-alert-candidates.component.ng1.ts:1169`), so a failed ATS write leaves no export
 * recorded. Calling it first marks candidates exported that never arrived.
 *
 * It answers the exported date, as a number.
 */
export async function exportCandidates(alertId: number, candidateIds: number[]): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-candidate/exportCandidates', { alertId, candidateIds })
}

/** `viewClientContact` — the contacts twin of `viewCandidate`, with the same ATS-note reach. */
export async function viewClientContact(alertId: number, clientContactId: number): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-client-contact/viewClientContact', {
    alertId,
    clientContactId,
  })
}

/** `exportClientContacts` — the contacts twin of `exportCandidates`; same ordering rule. */
export async function exportClientContacts(alertId: number, clientContactIds: number[]): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-client-contact/exportClientContacts', {
    alertId,
    clientContactIds,
  })
}

/**
 * The contacts twins of `favorCandidates` / `hideCandidates` and their singular inverses
 * (`extension-alert-client-contact.api.ng1.ts:509-580`).
 *
 * ⚠️ THE PARAMETER IS `clientContactIds`, NOT `candidateIds`, and the path segment is
 * `alert-client-contact`. Same shapes, same asymmetry — bulk favour and hide, singular
 * unfavour and unhide — because the selection bar acts on a list and the row acts on one.
 */
export async function favorClientContacts(alertId: number, clientContactIds: number[]): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-client-contact/favorClientContacts', {
    alertId,
    clientContactIds,
  })
}

/** Returns the `hiddenDate` the SERVER chose, as the candidate call does. */
export async function hideClientContacts(alertId: number, clientContactIds: number[]): Promise<number> {
  return request<number>('POST', '/chrome-extension-api/alert-client-contact/hideClientContacts', {
    alertId,
    clientContactIds,
  })
}

/** ⚠️ SINGULAR — `clientContactId`. */
export async function unhideClientContact(alertId: number, clientContactId: number): Promise<void> {
  await request('POST', '/chrome-extension-api/alert-client-contact/unhideClientContact', {
    alertId,
    clientContactId,
  })
}

/** ⚠️ SINGULAR TOO. */
export async function unfavorClientContact(alertId: number, clientContactId: number): Promise<void> {
  await request('POST', '/chrome-extension-api/alert-client-contact/unfavorClientContact', {
    alertId,
    clientContactId,
  })
}

/** `getCandidateNumbersByAlertId` — the per-alert counts the tab strip and list header show. */
export async function getCandidateNumbersByAlertId(alertId: number): Promise<{
  totalResults: number
  totalNewTopMatchesResults: number
  totalNewTopMatchesSinceLastViewedAlerts: number
}> {
  return request('POST', '/chrome-extension-api/alert-candidate/getCandidateNumbersByAlertId', { alertId })
}

/** `IAlertClientContactListDto` — the contacts behind a client-contact alert. */
export interface AlertClientContact {
  id: number
  /** Optional for the same reason as `AlertCandidate.name`; `candidateName` fills it in. */
  name?: string | null
  externalId?: string
  algorithm?: number
  lastPosition?: string | null
  lastCompany?: string | null
  location?: string | null
  /** Read by the messaging check: no email, or a blacklisted one, cannot be messaged. */
  email?: string | null
  emailBlacklisted?: boolean | null
  hidden?: boolean
  viewed?: boolean
  favorite?: boolean
  messaged?: boolean
  exported?: boolean
  atsInfo?: {
    contactHighestActivityLevel?: string | null
    contactHighestActivityCount?: number | null
    /** Epoch millis — the contact row's date column, where a candidate shows its CV date. */
    contactLastActivityDate?: number | null
  } | null
  /** Epoch millis. */
  lastReceivedMessage?: number | null
}

export interface AlertClientContactList {
  clientContactPage: AlertClientContact[]
  maxResults?: number
}

/**
 * `getClientContactsByAlertId` — the contacts twin of `getCandidatesByAlertId`, and a POST for
 * the same reason.
 *
 * ⚠️ NOT FOLDED INTO ONE GENERIC FUNCTION WITH THE CANDIDATE CALL, unlike `getAlerts`. The
 * alert endpoints differ only by a path segment and return the same container; these two
 * return DIFFERENT shapes under different keys (`candidatePage` against `clientContactPage`)
 * with different entity fields. A shared function would have to return a union its callers
 * immediately narrow, which is the abstraction costing more than it saves.
 */
export const AlertClientContactSortBy = {
  RelevancyPastActivity: 'RELEVANCY_PAST_ACTIVITY',
  RelevancyHiringLikelihood: 'RELEVANCY_HIRING_LIKELIHOOD',
} as const
export type AlertClientContactSortBy =
  (typeof AlertClientContactSortBy)[keyof typeof AlertClientContactSortBy]

/**
 * `IAlertClientContactFilter` — the contacts twin of `AlertCandidateFilter`, and every field
 * is spelled differently. There is no `lastRelevancyXDays`: the contacts bar has no CV-date
 * control, so the only filter is which group is shown.
 */
export interface AlertClientContactFilter {
  showFavoriteClientContactsOnly?: boolean
  showHiddenClientContacts?: boolean
  hideViewedClientContacts?: boolean
  showExportedClientContactsOnly?: boolean
  showMessagedClientContactsOnly?: boolean
  showActionedAndUnactionedClientContacts?: boolean
}

export async function getClientContactsByAlertId(params: {
  alertId: number
  filter?: AlertClientContactFilter
  sortBy?: AlertClientContactSortBy
  persistFilterAndSort?: boolean
}): Promise<AlertClientContactList> {
  return request<AlertClientContactList>(
    'POST',
    '/chrome-extension-api/alert-client-contact/getClientContactsByAlertId',
    params,
  )
}

/**
 * `getPrediction` (extension-alert-candidate.api.ng1.ts:817) — how many candidates the criteria
 * currently in the form would match, and how many of those are top matches.
 *
 * It is what the edit screen's left rail shows above Save, and it is PUSHED into the alert tab
 * rather than fetched by it: the criteria form calls this after every change and publishes the
 * answer.
 */
export interface ExtensionCriteriaPrediction {
  results: number
  topMatches: number
}

export interface GetPredictionParams {
  /**
   * ⚠️ `null` FOR A NEW ALERT, NOT OMITTED. Upstream sends
   * `isEditingExistingAlert ? alert.id : null` — the server distinguishes "predict for this
   * saved alert" from "predict for criteria not yet attached to one", and leaving the key out
   * is a third thing.
   */
  alertId: number | null
  extensionCriteria: Readonly<Record<string, unknown>>
  /**
   * `getFiltersForCountersAndPrediction` — `{ showHiddenCandidates: false, atsFilters }`, and
   * that `false` is explicit rather than defaulted.
   */
  filter?: { showHiddenCandidates: boolean; atsFilters?: unknown }
}

export async function getExtensionCriteriaPrediction(
  params: GetPredictionParams,
): Promise<ExtensionCriteriaPrediction> {
  return request('POST', '/chrome-extension-api/alert-candidate/getPrediction', params)
}

/**
 * The contacts twin — `alert-client-contact/getPrediction`.
 *
 * ⚠️ IT RETURNS ONE NUMBER, NOT TWO. `IClientContactExtensionCriteriaPredictionDto` is
 * `{ results }` alone, where the candidate DTO adds `topMatches`, and the contact component
 * forwards only `predictionCount: results`. So the edit tab's progress bar has no second value
 * to plot against and reads full — which is exactly what the reference capture shows, at
 * 102 over 102. Typing this as the candidate shape and reading `topMatches` gives `undefined`
 * and an empty bar.
 */
export async function getClientContactCriteriaPrediction(params: {
  alertId: number | null
  extensionCriteria: Readonly<Record<string, unknown>>
  filter?: { showHiddenCandidates: boolean; atsFilters?: unknown }
}): Promise<{ results: number }> {
  return request('POST', '/chrome-extension-api/alert-client-contact/getPrediction', params)
}

// ── The candidate PROFILE screen (plan §7 phase 3, slice 4) ──

/** `IAlertCandidateAtsNoteDto`. */
export interface AlertCandidateAtsNote {
  dateAdded?: number | null
  submitterName?: string | null
  text: string
}

/** `IAlertCandidateAtsInfoDto` — what the "<ATS> information" card reads. */
export interface AlertCandidateAtsInfo {
  latestStatus?: { text: string; date?: number | null } | null
  ownerFullName?: string | null
  externalCreationDate?: number | null
  externalCandidateNote?: AlertCandidateAtsNote | null
}

/**
 * The profile's candidate — `IAlertCandidateDto`, which is `AlertCandidate` plus the fields
 * only the full profile shows.
 *
 * ⚠️ IT IS THE SAME ENDPOINT'S SHAPE, NOT A RICHER ONE. `getCandidatesIgnoreFiltersAlertless`
 * and `getCandidatesByAlertId` both return `IAlertCandidateDto`; the slim list simply reads
 * eleven of its fields. So this extends rather than replaces, and the list keeps its narrow
 * view of the same rows.
 */
export interface AlertCandidateProfile extends AlertCandidate {
  phoneNumber?: string | null
  /** Which index version wrote the row; picks `validationDate` over the older date fields. */
  indexVersion?: number | null
  validationDate?: number | null
  lastRelevancyDate?: number | null
  enrichedExtraExperience?: boolean | null
  enrichedRecordsAdded?: number | null
  socialUrls?: { linkedinUrl?: string | null; githubUrl?: string | null; dribbleUrl?: string | null } | null
  atsInfo?: AlertCandidateAtsInfo | null
}

/**
 * `getCandidatesIgnoreFiltersAlertless` — one candidate by id, with NO alert behind it.
 *
 * ⚠️ "IGNORE FILTERS" IS THE POINT, NOT AN OPTION. The profile is reachable from a link and
 * from inside an ATS page, where no alert and no filter exist — asking the filtered list for a
 * candidate it has hidden or archived returns an empty page, and the screen then shows "not
 * found". `EntityContainerBase.getEntity` picks between this and the alert-scoped call on
 * whether `$stateParams.alertId` is a NUMBER, so a caller that passes the id as a string
 * silently takes the other branch.
 */
export async function getCandidatesIgnoreFiltersAlertless(params: {
  limitCandidateIds: number[]
}): Promise<{ candidatePage: AlertCandidateProfile[]; maxResults?: number }> {
  return request('POST', '/chrome-extension-api/alert-candidate/getCandidatesIgnoreFiltersAlertless', params)
}

/**
 * The profile's contact — `IAlertClientContactDto`, which is `AlertClientContact` plus the
 * fields only the full profile shows.
 *
 * ⚠️ ITS `atsInfo` IS NOT THE CANDIDATE'S. `ownerName` and `status` here against
 * `ownerFullName` and `latestStatus` there, over the same two labels on screen — see
 * `ClientContactAtsInfoCard`.
 */
export interface AlertClientContactProfile extends AlertClientContact {
  phoneNumber?: string | null
  /** `IAlertClientContactDto.phoneNumbers` — what the profile shows, joined with ", ". */
  phoneNumbers?: string[] | null
  /** `ClientContactDetailsSource` (0 is the ATS) for the email and for the phones. */
  emailSource?: number | null
  phoneNumberSource?: number | null
  lastTimeUserRequestedContactDetails?: number | null
  linkedinUrl?: string | null
  /** The contact's own location — what "Create new contact" sends as `city`/`region`/`country`. */
  city?: string | null
  region?: string | null
  country?: string | null
  /**
   * `IAlertClientContactCompanyDataDto[]` — NOT the preview's `CurrentCompanyData`, which is a
   * different DTO from a different call. Only the `addedByWoo` ones can be pushed to the ATS, and
   * "Create in ATS" is disabled without one.
   */
  currentCompanies?: AlertClientContactCompanyData[] | null
  /** A company switch was detected; read by the disabled Create-in-ATS tooltip (woo@c627765c72). */
  switchedCompany?: boolean | null
  atsInfo?: {
    ownerName?: string | null
    status?: string | null
    contactHighestActivityLevel?: string | null
    contactHighestActivityCount?: number | null
    contactLastActivityName?: string | null
    contactLastActivityDate?: number | null
    /** `IAlertClientContactAtsInfoDto.comments` — Vincere's "Brief". */
    comments?: string | null
    /** `IAlertClientContactNoteDto[]`. */
    lastNotes?: { dateAdded?: number | null; submitterName?: string | null; text: string }[] | null
  } | null
}

/** `IAlertClientContactCompanyDataDto` (`extension-alert-client-contact.api.ng1.ts:96`). */
export interface AlertClientContactCompanyData {
  companyId?: number | null
  companyName: string
  jobTitle?: string | null
  addedByWoo: boolean
  city?: string | null
  region?: string | null
  country?: string | null
}

/**
 * `getClientContactsIgnoreFiltersAlertless` — the contacts twin of the candidate call.
 *
 * ⚠️ THE PARAMETER IS `limitClientContactIds`, NOT `limitCandidateIds`, and there is a second
 * one — `limitClientContactExternalIds` — because a contact can be addressed by the ATS's own
 * id as well as by ours. Both are optional, and sending neither returns the unfiltered page.
 */
export async function getClientContactsIgnoreFiltersAlertless(params: {
  limitClientContactIds?: number[]
  limitClientContactExternalIds?: string[]
}): Promise<{ clientContactPage: AlertClientContactProfile[]; maxResults?: number }> {
  return request('POST', '/chrome-extension-api/alert-client-contact/getClientContactsIgnoreFiltersAlertless', params)
}

/** `getCandidateByAlertIdIgnoreFilters` — the same candidate, within an alert's context. */
export async function getCandidateByAlertIdIgnoreFilters(params: {
  alertId: number
  candidateId: number
}): Promise<{ candidatePage: AlertCandidateProfile[]; maxResults?: number }> {
  return request('POST', '/chrome-extension-api/alert-candidate/getCandidateByAlertIdIgnoreFilters', params)
}

/**
 * `getClientContactByAlertIdIgnoreFilters` — the contacts twin of the call above.
 *
 * ⚠️ WHICH OF THE TWO A SCREEN CALLS IS DECIDED BY ONE `angular.isNumber` UPSTREAM.
 * `EntityContainerBase.getEntity` picks this when `$stateParams.alertId` is a NUMBER and the
 * alertless call otherwise — so the same container serves the standalone profile and the one
 * inside the review's side view, and the only difference is whether the URL carried an alert.
 * Note `isNumber`, not truthiness: the state declares `alertId` with `type: 'int'`, so a route
 * that forgets the type gets the string "19" here and silently takes the alertless branch.
 */
export async function getClientContactByAlertIdIgnoreFilters(params: {
  alertId: number
  clientContactId: number
}): Promise<{ clientContactPage: AlertClientContactProfile[]; maxResults?: number }> {
  return request('POST', '/chrome-extension-api/alert-client-contact/getClientContactByAlertIdIgnoreFilters', params)
}

/** `IProfilePreviewDataDTO` and its three arrays — the profile's experience/education/skills. */
export interface CandidateProfilePreview {
  previewData?: {
    experience?: Array<{
      jobTitle?: string | null
      jobEmployer?: string | null
      jobDescription?: string | null
      jobPeriod?: string | null
      jobEmployerLocation?: string | null
      logoUrl?: string | null
      addedByWoo?: boolean
    }> | null
    skills?: Array<{ name?: string | null; period?: string | null; current?: boolean }> | null
    education?: Array<{
      degreeType?: string | null
      fieldOfStudy?: string | null
      institute?: string | null
      year?: number | null
    }> | null
  }
  /** `IAlertCandidateDescriptionSummaryDto` — the hunter screens' card; unported so far. */
  matchingSummary?: { matchingSummaryItems?: unknown[] } | null
}

/**
 * `getCandidateProfilePreviewAndMatchingSummary`.
 *
 * ⚠️ THE PARAMETERS GO IN THE BODY AND `params` IS SENT EXPLICITLY EMPTY — the exact inverse of
 * `saveAlert`, which puts everything in the query string. Upstream writes `params: {}, data:
 * params`, and sending `{alertId, candidateId}` as a query string instead answers
 * `400 {"error":"Invalid request sent"}`. Two endpoints on one controller, two conventions:
 * read each `*.api.ng1.ts` rather than pattern-matching off its neighbour.
 *
 * ⚠️ AND `alertId` IS OPTIONAL. The standalone profile has no alert, and the server answers
 * with the preview and no matching summary — which is right, since the summary is a statement
 * about a search this candidate has not been matched against.
 */
export async function getCandidateProfilePreviewAndMatchingSummary(params: {
  candidateId: number
  alertId?: number
}): Promise<CandidateProfilePreview> {
  return request('POST', '/chrome-extension-api/alert-candidate/getCandidateProfilePreviewAndMatchingSummary', params)
}

/** `HiringLikelihoodLevel` — the third column of the "Latest company" table. */
export const HiringLikelihoodLevel = { Unknown: 'Unknown', Low: 'Low', High: 'High' } as const
export type HiringLikelihoodLevel = (typeof HiringLikelihoodLevel)[keyof typeof HiringLikelihoodLevel]

/** `ICurrentCompanyDataDto` — one row of that table. */
export interface CurrentCompanyData {
  name: string
  title?: string | null
  hiringLikelihood?: HiringLikelihoodLevel | null
  enrichmentRelevancyDate?: number | null
  logoUrl?: string | null
  companyIndustries?: string[] | null
  companySize?: string | null
  companyLinkedinUrl?: string | null
  /** Whether Woo added the company rather than the ATS — the header's "Added by Woo" note. */
  addedByWoo?: boolean
}

/** `IAdditionalCompanyDataDto` — the contact's earlier companies, listed under the current. */
export interface AdditionalCompanyData {
  name?: string | null
  title?: string | null
  logoUrl?: string | null
  startDate?: number | null
  endDate?: number | null
}

/** `IClientContactPreviewAndSummaryDto` — both halves of the contact's "More" view. */
export interface ClientContactPreviewAndSummary {
  currentCompanies?: CurrentCompanyData[]
  additionalCompanies?: AdditionalCompanyData[]
}

/**
 * `getClientContactProfilePreviewAndMatchingSummaryV2` — the companies behind a contact.
 *
 * ⚠️ IT IS THE **V2**, AND THE NAME IS THE ONLY THING IT SHARES WITH THE CANDIDATE CALL. The
 * candidate endpoint answers a CV preview and a matching summary; this one answers companies —
 * `currentCompanies` and `additionalCompanies` — and nothing else. Reading it as the candidate
 * shape gives `previewData: undefined` and an empty screen with no error.
 *
 * ⚠️ AND `alertId` IS REQUIRED HERE. The container takes both ids straight off `$stateParams`,
 * so the standalone contact profile — which has no alert — sends `undefined` and the server
 * answers for the contact alone. That is upstream's own behaviour, not a guard to add.
 */
export async function getClientContactPreviewAndSummary(params: {
  clientContactId: number
  alertId?: number
}): Promise<ClientContactPreviewAndSummary> {
  return request(
    'POST',
    '/chrome-extension-api/alert-client-contact/getClientContactProfilePreviewAndMatchingSummaryV2',
    params,
  )
}

/**
 * `downloadWooProfile` — the strip's download icon
 * (`shortlist.candidate-actions.service.ng1.ts:78`). A GET that answers a PDF; the only read
 * in the strip that produces a file rather than a screen, which is why it is here and the
 * other four outward actions are not.
 */
export async function downloadWooProfile(candidateId: number): Promise<DownloadedFile> {
  return fetchFile(
    `/chrome-extension-api/alert-candidate/downloadWooProfile?${new URLSearchParams({ candidateId: String(candidateId) }).toString()}`,
    { fallbackFilename: 'profile.pdf', failureMessage: 'The profile could not be downloaded.' },
  )
}

/** `$httpProvider.defaults.headers.common.Accept`. */
const ANGULAR_ACCEPT = 'application/json, text/plain, */*'

/**
 * `sendCandidateDetails` (`extension-alert-candidate.api.ng1.ts:874`) — "Email candidate
 * details to me": the server mails the profile to the signed-in recruiter.
 *
 * ⚠️ `candidateId` IS A QUERY PARAMETER AND THERE IS NO BODY. Upstream passes it in `params`,
 * not `data`, so a JSON body carrying it is a different request.
 *
 * ⚠️ AND NO CONTENT-TYPE, although upstream's config SETS `'Content-Type': 'text/plain'`.
 * AngularJS 1.3.18's `$http` (`vendor/angular/angular.js:9401`, "strip content-type if data is
 * undefined") deletes that header whenever there is no data, so what production actually sends
 * is a bare POST — and `request()` sends exactly that for an `undefined` body.
 *
 * The answer is `$http<string>`'s `res.data`, which no caller reads: success and failure are
 * all the confirmation dialogue shows.
 *
 * ⚠️ AND THE ACCEPT HEADER IS ANGULAR'S, NOT `request()`'s. The endpoint produces text, and with
 * `accept: application/json` alone the tenant answers **406** — measured 28 Oct: the port's
 * dialogue showed the support line where build 354's said the email was on its way. AngularJS
 * sends `application/json, text/plain, *\/*` on every request, which is what lets it through.
 */
export async function sendCandidateDetails(candidateId: number): Promise<void> {
  await request<unknown>(
    'POST',
    `/chrome-extension-api/alert-candidate/sendCandidateDetails?${new URLSearchParams({ candidateId: String(candidateId) }).toString()}`,
    undefined,
    { headers: { accept: ANGULAR_ACCEPT } },
  )
}

/** `IAlertClientContactDetailsDto` — the same three fields as the candidate twin. */
export interface AlertClientContactDetails {
  id: number
  name?: string
  blacklistReason?: string
}

export interface AlertClientContactDetailsList {
  details: AlertClientContactDetails[]
  /** As on the candidate side — `HunterResultsLoaded` reports it. */
  totalEnriched?: number
  maxResults?: number
}

/**
 * `getClientContactDetailsByAlertId` — ids and names for the WHOLE result set, which is what
 * "Select all results" needs and what the row fetch deliberately does not return.
 *
 * ⚠️ IT TAKES THE SAME PARAMS AS THE LIST, and must be given them: sending a different filter
 * than the list is showing selects contacts the recruiter cannot see.
 */
export async function getClientContactDetailsByAlertId(params: {
  alertId: number
  filter?: AlertClientContactFilter
  sortBy?: AlertClientContactSortBy
}): Promise<AlertClientContactDetailsList> {
  return request<AlertClientContactDetailsList>(
    'POST',
    '/chrome-extension-api/alert-client-contact/getClientContactDetailsByAlertId',
    params,
  )
}

/** `IExtensionCriteriaAtsFilterValueData` — the shape every criteria autocomplete answers. */
export interface CriteriaAutocompleteValue {
  text?: string
  value?: string
}

/**
 * `getHighestActivityLevelsAutocompleteValues` — the contact builder's "Previous engagement"
 * field, which is `add-from-autocomplete-only` and so could not be filled without it.
 *
 * ⚠️ A GET WITH THE QUERY IN THE QUERY STRING, named `searchQuery` rather than `query`.
 */
export async function getHighestActivityLevels(searchQuery: string): Promise<CriteriaAutocompleteValue[]> {
  return request(
    'GET',
    `/chrome-extension-api/client-contact-alert/getHighestActivityLevelsAutocompleteValues?${new URLSearchParams({ searchQuery }).toString()}`,
  )
}

/**
 * `getClientContactNumbersByAlertId` — the contacts twin of
 * `getCandidateNumbersByAlertId`, and the counts the alert tabs show.
 *
 * ⚠️ THE BODY IS THE ALERT PARAMS OBJECT, NOT A BARE ID. Upstream posts
 * `IGetAlertClientContactParams` — the same `{ alertId, filter? }` shape the list call takes —
 * where the candidate endpoint takes `{ alertId }`. Two sibling endpoints, two shapes.
 */
export async function getClientContactNumbersByAlertId(alertId: number): Promise<{
  totalResults: number
  totalNewTopMatchesResults: number
  totalNewTopMatchesSinceLastViewedAlerts: number
}> {
  return request('POST', '/chrome-extension-api/alert-client-contact/getClientContactNumbersByAlertId', {
    alertId,
  })
}
